import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserRole } from '@/shared/types';
import { api } from '@/lib/api';

export interface UserRow {
  id: string;
  email: string;
  role: UserRole;
  status: 'active' | 'disabled';
  created_at: string;
}

export interface UserCreateBody {
  email: string;
  password: string;
  role: UserRole;
  status?: 'active' | 'disabled';
}

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api<{ rows: UserRow[] }>('/users'),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreateBody) => api<UserRow>('/users', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<UserCreateBody> }) =>
      api<UserRow>(`/users/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}
